<template>
  <v-dialog
    :max-width="$vuetify.breakpoint.xs ? '95vw' : '800px'"
    persistent
    v-model="dialog"
    scrollable
    :fullscreen="$vuetify.breakpoint.xs"
  >
    <v-card
      class="new-address-dialog-card"
      :class="{ 'mobile-card': $vuetify.breakpoint.xs }"
      v-if="selectedCustomer != null"
    >
      <!-- Modern Header -->
      <div class="address-header">
        <div class="header-icon" v-if="!$vuetify.breakpoint.xs">
          <v-icon color="#fddf00" size="32">add_location</v-icon>
        </div>
        <div class="header-content">
          <div class="header-title-row">
            <v-icon
              v-if="$vuetify.breakpoint.xs"
              color="#fddf00"
              size="24"
              class="mobile-header-icon"
              >add_location</v-icon
            >
            <h2 class="header-title">
              {{ $vuetify.breakpoint.xs ? "Add Address" : "Add New Address" }}
            </h2>
          </div>
          <p class="header-subtitle" v-if="!$vuetify.breakpoint.xs">
            Add a new address for {{ selectedCustomer.user_first_name }}
            {{ selectedCustomer.user_last_name }}
          </p>
        </div>
        <v-btn icon @click="cancel()" class="close-btn">
          <v-icon>close</v-icon>
        </v-btn>
      </div>

      <v-divider></v-divider>

      <!-- Form Content -->
      <v-card-text class="dialog-content">
        <v-form ref="form" v-model="valid" lazy-validation class="address-form">
          <!-- Address Name -->
          <div class="form-section">
            <h4 class="section-title">
              <v-icon color="#fddf00" class="mr-2">label</v-icon>
              Address Information
            </h4>

            <v-text-field
              v-model="address_name"
              label="Address Name"
              outlined
              color="#fddf00"
              :rules="[validation.required]"
              :disabled="isEditCustomer"
              placeholder="e.g., Home, Office, Shop"
              class="form-field"
            >
              <template v-slot:prepend-inner>
                <v-icon color="#fddf00" size="20">label</v-icon>
              </template>
            </v-text-field>

            <v-textarea
              v-model="address"
              label="Full Address"
              outlined
              rows="3"
              color="#fddf00"
              :rules="[validation.required]"
              :disabled="isEditCustomer"
              placeholder="Enter the complete address"
              class="form-field"
            >
              <template v-slot:prepend-inner>
                <v-icon color="#fddf00" size="20">home</v-icon>
              </template>
            </v-textarea>
          </div>

          <!-- Location Selection -->
          <div class="form-section">
            <h4 class="section-title">
              <v-icon color="#fddf00" class="mr-2">map</v-icon>
              Location Details
            </h4>

            <div class="location-grid">
              <v-select
                :items="provinceItems"
                v-model="province_add_id"
                @change="onChangeProvince"
                item-text="province_name_th"
                item-value="province_id"
                label="Province"
                outlined
                color="#fddf00"
                :rules="[validation.required]"
                :disabled="isEditCustomer"
                class="form-field"
              >
                <template v-slot:prepend-inner>
                  <v-icon color="#fddf00" size="20">map</v-icon>
                </template>
              </v-select>

              <v-select
                :items="districtItems"
                v-model="district_add_id"
                @change="onChangeDistrict"
                item-text="district_name_th"
                item-value="district_id"
                label="District"
                outlined
                color="#fddf00"
                :rules="[validation.required]"
                :disabled="isEditCustomer"
                class="form-field"
              ></v-select>

              <v-select
                :items="subDistrictItems"
                v-model="subDistrict_add_id"
                item-text="sub_district_name_th"
                item-value="sub_district_id"
                label="Sub District"
                outlined
                color="#fddf00"
                :rules="[validation.required]"
                :disabled="isEditCustomer"
                class="form-field"
              ></v-select>

              <v-select
                :items="postcodeItems"
                v-model="zipcode_add_id"
                item-text="zipcode_code"
                item-value="zipcode_id"
                label="Postal Code"
                outlined
                color="#fddf00"
                :rules="[validation.required]"
                :disabled="isEditCustomer"
                class="form-field"
              ></v-select>
            </div>
          </div>

          <!-- Coordinates Section -->
          <div class="form-section">
            <h4 class="section-title">
              <v-icon color="#fddf00" class="mr-2">my_location</v-icon>
              Coordinates & Map
            </h4>

            <div class="coordinates-grid">
              <v-text-field
                v-model="address_lat"
                label="Latitude"
                outlined
                color="#fddf00"
                :rules="[rules.required, rules.onlyDouble]"
                :disabled="isEditCustomer"
                class="form-field"
                hint="Drag the map marker to set coordinates"
              >
                <template v-slot:prepend-inner>
                  <v-icon color="#fddf00" size="20">my_location</v-icon>
                </template>
              </v-text-field>

              <v-text-field
                v-model="address_long"
                label="Longitude"
                outlined
                color="#fddf00"
                :rules="[rules.required, rules.onlyDouble]"
                :disabled="isEditCustomer"
                class="form-field"
              >
                <template v-slot:prepend-inner>
                  <v-icon color="#fddf00" size="20">location_on</v-icon>
                </template>
              </v-text-field>
            </div>

            <div class="map-controls">
              <v-btn
                @click="change_location++"
                :disabled="isEditCustomer"
                color="#fddf00"
                outlined
                class="map-update-btn"
              >
                <v-icon class="mr-2">refresh</v-icon>
                Update Map View
              </v-btn>
            </div>

            <!-- Map -->
            <div class="map-container">
              <GoogleMap
                :latitude="parseFloat(address_lat) || 13.7563"
                :longitude="parseFloat(address_long) || 100.5018"
                :change="change_location"
                :height="300"
                :zoom="15"
                :mapType="'all'"
                :draggable="true"
                @draged="getDragLocation($event)"
              />
            </div>
          </div>

          <!-- Additional Notes -->
          <div class="form-section">
            <h4 class="section-title">
              <v-icon color="#fddf00" class="mr-2">note</v-icon>
              Additional Information
            </h4>

            <v-textarea
              v-model="address_note"
              label="Address Notes"
              outlined
              rows="2"
              color="#fddf00"
              :disabled="isEditCustomer"
              placeholder="Delivery instructions, landmarks, etc. (Optional)"
              class="form-field"
              hint="Optional - any additional information about this address"
            >
              <template v-slot:prepend-inner>
                <v-icon color="#fddf00" size="20">note</v-icon>
              </template>
            </v-textarea>
          </div>
        </v-form>
      </v-card-text>

      <!-- Action Buttons -->
      <v-card-actions class="dialog-actions">
        <v-btn
          @click="cancel()"
          color="grey"
          outlined
          class="action-btn cancel-btn"
        >
          <v-icon class="mr-2" color="white">cancel</v-icon>
          <span class="cancel-btn-text">Cancel</span>
        </v-btn>

        <v-btn
          @click="clearForm()"
          :disabled="isEditCustomer"
          color="orange"
          outlined
          class="action-btn clear-btn"
        >
          <v-icon class="mr-2" color="white">clear</v-icon>
          <span class="clear-btn-text">Clear Form</span>
        </v-btn>

        <v-spacer></v-spacer>

        <v-btn
          @click="addAddress()"
          :disabled="isEditCustomer || !valid || isAdding"
          :loading="isAdding"
          color="#fddf00"
          class="action-btn add-btn"
        >
          <v-icon class="mr-2">add_location</v-icon>
          Add Address
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState, mapGetters } from "vuex";

export default {
  props: ["dialog", "selectedCustomer"],
  data() {
    return {
      address_name: "",
      address: "",
      address_note: "",
      address_lat: 13.7563, // Default to Bangkok
      address_long: 100.5018,
      province_add_id: null,
      district_add_id: null,
      subDistrict_add_id: null,
      zipcode_add_id: null,
      change_location: 0,
      valid: false,
      isAdding: false,
      rules: {
        onlyDouble: (value) => {
          const pattern = /^-?([0-9]?[0-9]?[0-9]?[0-9]|[0-9]0)\.{1}\d{1,100}$/;
          return pattern.test(value) || "Invalid coordinate format";
        },
        required: (value) => !!value || "This field is required",
      },
      validation: {
        required: (value) => !!value || "This field is required",
      },
    };
  },
  computed: {
    ...mapState({
      provinceItems: (state) => state.fetchCustomerBack.provinceAdd,
      districtItems: (state) => state.fetchCustomerBack.districtAdd,
      subDistrictItems: (state) => state.fetchCustomerBack.subDistrictAdd,
      postcodeItems: (state) => state.fetchCustomerBack.zipcodeAdd,
    }),
    ...mapGetters(["isEditCustomer"]),
  },
  watch: {
    dialog(newVal) {
      if (newVal) {
        // Load province data when dialog opens
        this.$store.dispatch("fetchCustomerBack/getProvinceAdd");
      } else {
        // Clear form when dialog closes
        this.clearForm();
      }
    },
  },
  methods: {
    getDragLocation(val) {
      this.address_lat = val.lat;
      this.address_long = val.lng;
    },

    onChangeDistrict() {
      this.subDistrict_add_id = null;
      this.zipcode_add_id = null;
      this.$store.dispatch(
        "fetchCustomerBack/getSubDistrictAdd",
        this.district_add_id
      );
      this.$store.dispatch(
        "fetchCustomerBack/getZipCodeAdd",
        this.district_add_id
      );
    },

    onChangeProvince() {
      this.subDistrict_add_id = null;
      this.district_add_id = null;
      this.zipcode_add_id = null;
      this.$store.dispatch(
        "fetchCustomerBack/getDistrictAdd",
        this.province_add_id
      );
    },

    async addAddress() {
      if (this.$refs.form.validate()) {
        this.isAdding = true;

        try {
          const info = {
            user_address_default: 0,
            user_address_contact_tel: "",
            user_address_text: this.address,
            user_address_lat: parseFloat(this.address_lat),
            user_address_long: parseFloat(this.address_long),
            user_address_sub_district: this.subDistrict_add_id,
            user_address_district: this.district_add_id,
            user_address_province: this.province_add_id,
            user_address_zipcode: this.zipcode_add_id,
            user_address_user: this.selectedCustomer.user_id,
            user_address_note: this.address_note || null,
            user_address_name: this.address_name,
          };

          const result = await this.$store.dispatch(
            "fetchCustomerBack/addCustomerAddress",
            info
          );

          if (result !== "error") {
            this.$emit("address-added");
            this.$emit("setDialog", false);
            this.clearForm();
          }
        } catch (error) {
          console.error("Error adding address:", error);
        } finally {
          this.isAdding = false;
        }
      }
    },

    clearForm() {
      this.address_name = "";
      this.address = "";
      this.address_note = "";
      this.address_lat = 13.7563;
      this.address_long = 100.5018;
      this.province_add_id = null;
      this.district_add_id = null;
      this.subDistrict_add_id = null;
      this.zipcode_add_id = null;
      this.change_location++;

      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },

    cancel() {
      this.clearForm();
      this.$emit("setDialog", false);
    },
  },
};
</script>

<style scoped>
.new-address-dialog-card {
  border-radius: 16px;
  overflow: hidden;
}

.mobile-card {
  border-radius: 0;
  height: 100vh;
  max-height: 100vh;
}

.address-header {
  display: flex;
  align-items: center;
  padding: 1.5rem 2rem;
  background: linear-gradient(135deg, #fddf00 0%, #f39c12 100%);
  gap: 1rem;
  position: relative;
}

.header-title-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.mobile-header-icon {
  margin-right: 0.5rem;
}

.close-btn {
  background: rgba(0, 0, 0, 0.1) !important;
  color: black !important;
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 10;
}

.dialog-content {
  padding: 2rem;
  /* Remove max-height restriction to allow natural height */
  /* max-height: 70vh; */
  /* overflow-y: auto; */
}

/* Remove scrollbar styling */
.dialog-content::-webkit-scrollbar {
  display: none;
}

.address-form {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.form-section {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid #e9ecef;
}

.section-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 1.5rem 0;
  display: flex;
  align-items: center;
}

.form-field {
  margin-bottom: 1rem;
}

.location-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.coordinates-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
}

.map-controls {
  margin-bottom: 1rem;
}

.map-update-btn {
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0;
}

.map-container {
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e9ecef;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.dialog-actions {
  padding: 1rem 2rem 1.5rem;
  background: #f8f9fa;
  border-top: 1px solid #e5e7eb;
  gap: 1rem;
}

.action-btn {
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0;
  min-width: 120px;
}

.add-btn {
  color: black !important;
  min-width: 140px;
}

.add-btn:disabled {
  opacity: 0.6 !important;
}

.cancel-btn {
  border-color: #9e9e9e !important;
}

.cancel-btn-text {
  color: white !important;
}

.clear-btn {
  border-color: #ff9800 !important;
}

.clear-btn-text {
  color: white !important;
}

/* Mobile Responsiveness */
@media (max-width: 768px) {
  .address-header {
    padding: 1rem 1.5rem;
    padding-top: 3rem;
  }

  .close-btn {
    top: 0.75rem;
    right: 0.75rem;
  }

  .dialog-content {
    padding: 1.5rem;
  }

  .form-section {
    padding: 1rem;
  }

  .location-grid,
  .coordinates-grid {
    grid-template-columns: 1fr;
  }

  .dialog-actions {
    padding: 1rem 1.5rem;
    flex-wrap: wrap;
  }

  .action-btn {
    min-width: auto;
    flex: 1;
  }
}

@media (max-width: 480px) {
  .address-header {
    padding: 0.75rem 1rem;
    padding-top: 2.5rem;
  }

  .close-btn {
    top: 0.5rem;
    right: 0.5rem;
    width: 40px;
    height: 40px;
  }

  .header-title {
    font-size: 1.2rem;
  }
}
</style>
